/**
 * Assinatura de quem está falando, no começo da mensagem.
 *
 * O cliente vê um número só — o da empresa — e do outro lado são sete pessoas
 * se revezando. Sem o nome, ele não sabe se está falando com quem falou ontem,
 * e passa a repetir o assunto do zero a cada resposta. O nome em negrito
 * resolve isso do lado dele, no aplicativo dele, sem depender de nada nosso.
 *
 * Os asteriscos são a sintaxe de negrito do WhatsApp, os dois-pontos marcam que
 * ali é quem fala e não o começo da mensagem, e a linha em branco separa uma
 * coisa da outra para não virar uma frase só ("*Mike*: Olá, tudo bem?").
 *
 * Fica aqui, e não no serviço de envio, porque quem assina são dois: a rota da
 * conversa, na api, e o disparo agendado, no worker. Uma regra escrita duas
 * vezes vira duas regras no dia em que alguém mexer numa só.
 */

/**
 * O primeiro nome, que é como as pessoas assinam conversa.
 *
 * "Mike Queiroz da Silva" vira "Mike": nome inteiro em cima de toda mensagem
 * parece papel timbrado, não gente respondendo. Se um dia dois vendedores
 * tiverem o mesmo primeiro nome, o lugar de resolver isso é aqui.
 */
export function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome.trim()
}

/**
 * Põe `*Nome*:` e uma linha em branco antes do texto.
 *
 * Não assina duas vezes: se o texto já começa com a assinatura de quem está
 * mandando, ele sai como está. Isso cobre o atalho de resposta rápida que já
 * vem com o nome escrito e o vendedor que digitou a assinatura na mão — nos
 * dois casos, assinar de novo entregaria "*Mike*" duplicado ao cliente.
 *
 * Texto vazio volta vazio: assinatura sozinha não é mensagem.
 */
export function assinar(conteudo: string, nome: string): string {
  const texto = conteudo.trim()
  if (texto.length === 0) return texto

  const assinatura = primeiroNome(nome)
  if (assinatura.length === 0) return texto

  if (jaAssinado(texto, assinatura)) return texto
  return `*${assinatura}*:\n\n${texto}`
}

/**
 * A primeira linha já é a assinatura desta pessoa?
 *
 * Compara sem diferenciar maiúscula de minúscula porque quem digita na pressa
 * escreve "*mike*", e uma assinatura repetida por causa de uma letra é um
 * defeito que só o cliente vê. Os dois-pontos são opcionais na comparação:
 * quem assina na mão escreve das duas formas, e as duas já são assinatura.
 */
function jaAssinado(texto: string, assinatura: string): boolean {
  const primeiraLinha = (texto.split('\n')[0] ?? '').trim().toLowerCase()
  const nome = assinatura.toLowerCase()
  return primeiraLinha === `*${nome}*` || primeiraLinha === `*${nome}*:`
}

/**
 * O texto sem a assinatura, para onde ela só atrapalha.
 *
 * Na prévia da lista de conversas o espaço é de uma linha, e ela já diz
 * "você:" antes do texto — repetir "*Mike*" ali gasta metade da linha para
 * dizer o que a outra metade já disse, e some justamente o começo da frase,
 * que é o que identifica a conversa.
 *
 * Tira qualquer primeira linha que seja só um nome em negrito, não apenas a
 * que este sistema escreveu: mensagem vinda do Digisac ou digitada na mão
 * chega com a mesma forma e merece o mesmo tratamento.
 */
export function semAssinatura(texto: string): string {
  return texto.replace(/^\*[^*\n]{1,40}\*:?\s*\n+/, '')
}
